import React, { useEffect, useRef, useState } from 'react';
import loadable from '@loadable/component';
import {
    HORIZONTAL_ORIENTATION,
    VERTICAL_ORIENTATION,
    GUEST_SELECT_SEARCH_DOCK_ID,
    GUEST_SELECT_SEARCH_FORM_ID,
    DATES_SELECT_SEARCH_FORM_ID,
    DATES_SELECT_SEARCH_DOCK_ID,
    ADRIATIC_LAST_SEARCH_PARAMS,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/constants';
import { startOfMonth, parseISO, set, isValid, differenceInDays } from 'date-fns';
import {
    formatLocalizedDate,
    formatDateForRequest,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/dateFormatService';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import {
    MdOutlinePlace,
    MdOutlineCalendarToday,
    MdPersonOutline,
    MdOutlineSearch,
    MdOutlineMyLocation,
    MdClose,
    MdApartment,
    MdInfoOutline,
    MdError,
    MdAdd,
    MdRemove,
} from 'react-icons/md';
import ModalControl from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/modalControl';
import { RiPencilLine } from 'react-icons/ri';
import Skeleton from 'react-loading-skeleton';
import { apiFetchData, apiPostDataWithConfig, getAdriaticLang, getAdriaticLangForDateFormats } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/apiService';
import runAfterNextPaint from '@PROJECT_ROOT/assets/responsive-frontend/js/services/runAfterNextPaint';
import { reportError } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/reportService';
import searchResultsWindowManipulationService from '@PROJECT_ROOT/assets/responsive-frontend/js/services/searchResultsWindowManipulationService';
import debounce from 'lodash/debounce';
import pageConfig from '@PROJECT_ROOT/responsiveFrontendPagesConfiguration.json';
import { IS_FRAME_SERVICE } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/frameService';
import { retryLoadableImport } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/retryService';
import {
    dispatchTrackingEvent,
    HOMEPAGE_QUICK_SEARCH_SUBMIT,
    HOMEPAGE_SEARCH_QUICK_LINK_CLICK_DETAILED,
    MOBILE_DOCK_QUICK_SEARCH_SUBMIT,
    PILLAR_PAGE_QUICK_SEARCH_SUBMIT,
    PILLAR_PAGE_SEARCH_QUICK_LINK_CLICK_DETAILED,
    SEARCH_ACTION, SEARCH_RESULTS_QUICK_SEARCH_SUBMIT,
    SEARCH_RESULTS_QUICK_SEARCH_SUBMIT_DETAILED,
    QUICK_SEARCH_SUGGESTED_LOCATION_SELECTED,
    MANUAL_INPUT_DATES_FROM_LOCAL_STORAGE,
    MANUAL_INPUT_DATES_OUTSIDE_LOCAL_STORAGE,
    QUICK_SEARCH_DATE_MODE_SELECTED,
    QUICK_SEARCH_LOCATION_UNIT_SELECTED,
    QUICK_SEARCH_LOCATION_PROPERTY_SELECTED,
} from '@PROJECT_ROOT/assets/responsive-frontend/js/services/tracking';
import { saveSearchParams, getSearchParams } from '@PROJECT_ROOT/assets/responsive-frontend/js/services/searchParamsStorageService';
import SegmentedControl from '@PROJECT_ROOT/assets/responsive-frontend/js/parts/SegmentedControl';
import useModalState from '@PROJECT_ROOT/assets/responsive-frontend/js/custom-hooks/useModalState';
import { QUICK_SEARCH_MODAL_DOCK_ID, QUICK_SEARCH_MODAL_ID } from '@PROJECT_ROOT/assets/responsive-frontend/js/constants/modalIds';

const DateRangePicker = loadable(
    () => retryLoadableImport(() => import(
        /* webpackChunkName: "responsiveFrontendDateRangePickerFromSearchForm" */'@PROJECT_ROOT/assets/responsive-frontend/js/parts/dateRangePicker'
    ))
        .catch((err) => {
            // TODO: Prebacit u service ako se pokaze korisno.
            err.customMessage = 'RETRY IMPORT-a - error sa importa DateRangePicker u searchForm komponenti.';
            reportError(err);
            return { default: () => '' };
        }),
);

export default function SearchForm({
    ssrParameters,
    ssrSearchForm,
    searchFormLoader,
    initialLocationData,
    allLocations,
    isHome,
    isHomeForFrame,
    isWishlist,
    isSearchResults,
    isSearchDock,
    isPillarPage,
    isAccordionVisible: isAccordionVisibleProp,
    closeAccordion: closeAccordionProp,
    update,
}) {
    const normalizeForSearch = (value) => (
        value
            .trim()
            .toLowerCase()
            .replace(/š/g, 's')
            .replace(/đ/g, 'd')
            .replace(/č/g, 'c')
            .replace(/ć/g, 'c')
            .replace(/ž/g, 'z')
    );

    const staticPathExtension = pageConfig.searchFormComponent.quickSearchFormPathExtension;
    const locationPathExtension = pageConfig.searchFormComponent.locationFormPathExtension;
    const datePathExtension = pageConfig.searchFormComponent.datePickFormPathExtension;
    const guestsPathExtension = pageConfig.searchFormComponent.numberOfGuestsFormPathExtension;
    const langISO = getAdriaticLang();
    const langISOForDateFormats = getAdriaticLangForDateFormats();
    const numberOfMonthsDisplayedDesktop = isPillarPage || isHomeForFrame ? 1 : 2;

    const [staticPublicResources, setStaticPublicResources] = useState(ssrSearchForm?.quickSearchResponse || '');
    const [locationPublicResources, setLocationPublicResources] = useState(ssrSearchForm?.locationFormResponse || '');
    const [datePublicResources, setDatePublicResources] = useState(ssrSearchForm?.datePickFormResponse || '');
    const [guestsPublicResources, setGuestsPublicResources] = useState(ssrSearchForm?.numberOfGuestsFormResponse || '');
    const [showModal, setShowModal] = useModalState(isSearchDock ? QUICK_SEARCH_MODAL_DOCK_ID : QUICK_SEARCH_MODAL_ID, 992);
    const [showLocation, setShowLocation] = useState(false);
    const [showDateRange, setShowDateRange] = useState(false);
    const [showGuests, setShowGuests] = useState(false);
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [hasScrolledFromTop, setHasScrolledFromTop] = useState(false);
    const [isAccordionVisible, setIsAccordionVisible] = useState(false);
    const [persons, setPersons] = useState(0);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [selectedDatesMemo, setSelectedDatesMemo] = useState({ startDate: null, endDate: null });
    const [calendarStartingMonth, setCalendarStartingMonth] = useState(startOfMonth(new Date()));
    const [persons_adult, setPersonsAdult] = useState(0);
    const [persons_12_17, setPersons12_17] = useState(0);
    const [persons_4_11, setPersons4_11] = useState(0);
    const [persons_0_3, setPersons0_3] = useState(0);
    const [p_dist, setPDist] = useState(0);
    const [userTouchedPDist, setUserTouchedPDist] = useState(false);
    const [searchedArray, setSearchedArray] = useState(allLocations ?? []);
    const [searchString, setSearchString] = useState('');
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [selectedLocationLabel, setSelectedLocationLabel] = useState(null);
    const [focusedLocationIndex, setFocusedLocationIndex] = useState(null);
    const [locationURL, setLocationURL] = useState('');
    const [savedLocationData, setSavedLocationData] = useState();
    const [isLoading, setIsLoading] = useState(!ssrSearchForm);
    const [isFetchingLocations, setIsFetchingLocations] = useState(false);
    const [isSmallDevice, setIsSmallDevice] = useState(false);
    const [isVerySmallDevice, setIsVerySmallDevice] = useState(false);
    const [wasModalEverOpen, setWasModalEverOpen] = useState(false);
    const [isFlexible, setIsFlexible] = useState(false);
    const [flexibleSwitchOptions, setFlexibleSwitchOptions] = useState(null);
    const [i_d, setIntervalDeviation] = useState(0);
    const [f_length, setFLength] = useState(0);
    const [shouldShowLessThanMinOvernightStaysError, setShouldShowLessThanMinOvernightStaysError] = useState(false);
    const [shouldShowMoreThanMaxOvernightStaysError, setShouldShowMoreThanMaxOvernightStaysError] = useState(false);
    const childrenCount = persons_0_3 + persons_4_11 + persons_12_17;
    const minAdults = childrenCount > 0 ? 1 : guestsPublicResources?.adults?.minimumNumber ?? 0;

    const [mainSearchFormElement, setMainSearchFormElement] = useState(null);
    const locationInput = useRef();
    const locationInputMobileRef = useRef();
    const guestsInput = useRef();
    const locationDropdownDesktopRef = useRef();
    const dateRangeWrapRef = useRef();
    const inputDateRangeRef = useRef();
    const flexibleDaysMinusButton = useRef();
    const flexibleDaysPlusButton = useRef();
    const fetchLocationsAbortController = useRef();
    const locationBlurTimeout = useRef(null);
    const startingTouchPosition = useRef();
    const isTouchMoving = useRef();
    const percentageMoved = useRef();
    const adultsLeftButton = useRef();
    const adultsRightButton = useRef();
    const children0to3LeftButton = useRef();
    const children0to3RightButton = useRef();
    const children4to11LeftButton = useRef();
    const children4to11RightButton = useRef();
    const children12to17LeftButton = useRef();
    const children12to17RightButton = useRef();

    const isLocationAnId = (value) => value && /\d/.test(value);
    const isSearchStringAllDigits = (value) => value && /^\d+$/.test(value);
    const isSearchStringValid = (value) => {
        if (!value) return false;
        const valueTrimmed = value.trim();
        if (valueTrimmed.length < 2) return false;
        const isAllDigits = isSearchStringAllDigits(valueTrimmed);
        if (isAllDigits && valueTrimmed.length < 3) return false;
        if (isLocationAnId(valueTrimmed) && !isAllDigits && valueTrimmed.length < 4) return false;
        return true;
    };

    const getData = () => {
        if (!staticPublicResources) {
            setIsLoading(true);
            const request1 = apiFetchData(staticPathExtension);
            const request2 = apiFetchData(datePathExtension);
            const request3 = apiFetchData(guestsPathExtension, '/0'); // 0 - inMoreThanOneUnit
            const request4 = apiFetchData(locationPathExtension);
            Promise.all([request1, request2, request3, request4])
                .then(([res1, res2, res3, res4]) => {
                    const responses = [res1, res2, res3, res4];
                    responses.forEach((currentRes) => {
                        if (currentRes.status !== 200) {
                            reportError(currentRes);
                        }
                    });
                    setStaticPublicResources(res1.data);
                    setDatePublicResources(res2.data);
                    setGuestsPublicResources(res3.data);
                    setLocationPublicResources(res4.data);
                })
                .catch((err) => {
                    reportError(err);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const hydrateStateFromStoredSearchParams = () => {
        const stored = getSearchParams();
        if (!stored) return;
        const {
            location_id: newLocationId,
            selectedLocation: newSelectedLocation,
            p_dist: newPDist,
            persons_adult: newPersonsAdult,
            persons_0_3: newPersons0to3,
            persons_4_11: newPersons4to11,
            persons_12_17: newPersons12to17,
            i_d: newInternalDeviation,
            f_length: newFLength,
            from: newFrom,
            to: newTo,
        } = stored;
        // DESC: #64588 - Parametar "selectedLocationLabel" se spremi samo na submit, prelaskom na drugu stranicu sa drugim jezikom,
        // parametar ostaje na jeziku kojem je spremljen, zbog toga preko location_id pogađamo odgovarajući prijevod.
        const allLocationsTemp = locationPublicResources.allLocations ?? [];
        const foundLocation = allLocationsTemp.find((location) => String(location.value) === String(newLocationId));
        const newLocationTitle = foundLocation?.title ?? null;

        const newStartDate = newFrom ? parseISO(newFrom) : null;
        const newEndDate = newTo ? parseISO(newTo) : null;

        setLocationURL(newLocationId || '');
        setSelectedLocation(newSelectedLocation || null);
        setSelectedLocationLabel(newLocationTitle ?? newSelectedLocation ?? null);
        setSearchString(newSelectedLocation || '');
        setPDist(newPDist || 0);
        setPersonsAdult(newPersonsAdult || 0);
        setPersons0_3(newPersons0to3 || 0);
        setPersons4_11(newPersons4to11 || 0);
        setPersons12_17(newPersons12to17 || 0);
        setIntervalDeviation(isWishlist ? 0 : newInternalDeviation || 0);
        setFLength(newFLength || 0);
        setIsFlexible(!isWishlist && newFLength !== undefined && newStartDate && newEndDate);
        setStartDate(newStartDate);
        setEndDate(newEndDate);

        update({
            location_id: newLocationId,
            from: newFrom,
            to: newTo,
            p_dist: newPDist,
            persons_adult: newPersonsAdult,
            persons_0_3: newPersons0to3,
            persons_4_11: newPersons4to11,
            persons_12_17: newPersons12to17,
            i_d: newInternalDeviation,
            f_length: newFLength,
        }, false);
    };

    const hydrateDatesAndGuestsForPillarPage = () => {
        const hasInitialDatesOrGuests = Boolean(initialLocationData?.to || initialLocationData?.persons_adult);
        const source = hasInitialDatesOrGuests ? initialLocationData : getSearchParams();
        if (!source) return;

        const {
            p_dist: newPDdist,
            persons_adult: newPersonsAdult,
            persons_0_3: newPersons0to3,
            persons_4_11: newPersons4to11,
            persons_12_17: newPersons12to17,
            i_d: newInternalDeviation,
            f_length: newFLength,
            from: newFrom,
            to: newTo,
        } = source;

        const newStartDate = newFrom ? parseISO(newFrom) : null;
        const newEndDate = newTo ? parseISO(newTo) : null;

        setPDist(newPDdist || 0);
        setPersonsAdult(newPersonsAdult || 0);
        setPersons0_3(newPersons0to3 || 0);
        setPersons4_11(newPersons4to11 || 0);
        setPersons12_17(newPersons12to17 || 0);
        setIntervalDeviation(isWishlist ? 0 : newInternalDeviation || 0);
        setFLength(newFLength || 0);
        setIsFlexible(!isWishlist && newFLength !== undefined && newStartDate && newEndDate);
        setStartDate(newStartDate);
        setEndDate(newEndDate);

        update({
            from: newFrom,
            to: newTo,
            p_dist: newPDdist,
            persons_adult: newPersonsAdult,
            persons_0_3: newPersons0to3,
            persons_4_11: newPersons4to11,
            persons_12_17: newPersons12to17,
            i_d: newInternalDeviation,
            f_length: newFLength,
        }, false);
    };

    const parseUrlParams = () => {
        const searchParams = new URLSearchParams(window.location.search);
        let hasUrlSearchParams = false;
        const location = searchResultsWindowManipulationService.getLocationName();
        const locationID = searchResultsWindowManipulationService.getLocationID();
        if (locationID !== null) {
            setLocationURL(locationID);
            setSelectedLocation(location);
            setSelectedLocationLabel(location);
            setSearchString(location);
        }
        let isStartDateSet = false;
        let isEndDateSet = false;
        let isFLengthSet = false;
        searchParams.forEach((value, key) => {
            hasUrlSearchParams = true;
            switch (key) {
                case 'from':
                {
                    const valueAsDate = parseISO(value);
                    const startDateParam = isValid(valueAsDate) ? valueAsDate : null;
                    if (startDateParam) {
                        setStartDate(startDateParam);
                        update({ from: value }, false);
                        isStartDateSet = true;
                    }
                    break;
                }
                case 'to':
                {
                    const valueAsDate = parseISO(value);
                    const endDateParam = isValid(valueAsDate) ? valueAsDate : null;
                    if (endDateParam) {
                        setEndDate(endDateParam);
                        update({ to: value }, false);
                        isEndDateSet = true;
                    }
                    break;
                }
                case 'p_dist':
                {
                    const pDistParam = parseInt(value, 10);
                    setPDist(pDistParam);
                    update({ p_dist: pDistParam }, false);
                    break;
                }
                case 'persons_adult':
                {
                    const personsAdultParam = parseInt(value, 10);
                    setPersonsAdult(personsAdultParam);
                    update({ persons_adult: personsAdultParam }, false);
                    break;
                }
                case 'persons_12_17':
                {
                    const persons12to17Param = parseInt(value, 10);
                    setPersons12_17(persons12to17Param);
                    update({ persons_12_17: persons12to17Param }, false);
                    break;
                }
                case 'persons_4_11':
                {
                    const persons4to11Param = parseInt(value, 10);
                    setPersons4_11(persons4to11Param);
                    update({ persons_4_11: persons4to11Param }, false);
                    break;
                }
                case 'persons_0_3':
                {
                    const persons0to3Param = parseInt(value, 10);
                    setPersons0_3(persons0to3Param);
                    update({ persons_0_3: persons0to3Param }, false);
                    break;
                }
                case 'locationId':
                case 'location_id':
                {
                    const locationIdParam = decodeURIComponent(value);
                    setLocationURL(locationIdParam);
                    update({ location_id: locationIdParam }, false);
                    break;
                }
                case 'location':
                    if (!isWishlist) {
                        const locationParam = decodeURIComponent(value);
                        setSelectedLocation(locationParam);
                        setSelectedLocationLabel(locationParam);
                        setSearchString(locationParam);
                    }
                    break;
                case 'f_length':
                {
                    if (!isWishlist) {
                        const parsedValue = Number(value);
                        const newValue = (Number.isInteger(parsedValue) && parsedValue >= 0 && parsedValue <= 31 ? parsedValue : 0);
                        setFLength(newValue);
                        update({ f_length: newValue }, false);
                        isFLengthSet = true;
                    }
                    break;
                }
                case 'i_d':
                {
                    if (!isWishlist) {
                        const parsedValue = Number(value);
                        const newValue = ([0, 3, 5, 7].includes(parsedValue) ? parsedValue : 0);
                        setIntervalDeviation(newValue);
                        update({ i_d: newValue }, false);
                    }
                    break;
                }
                default:
                    break;
            }
            if (isFLengthSet && isStartDateSet && isEndDateSet) {
                setIsFlexible(true);
            }
        });

        const hasInitialLocation = Boolean(initialLocationData?.id || initialLocationData?.value);
        if (!hasUrlSearchParams && !hasInitialLocation && locationID === null && !isSearchResults && !isWishlist) {
            hydrateStateFromStoredSearchParams();
        } else if (!hasUrlSearchParams && isPillarPage && hasInitialLocation) {
            hydrateDatesAndGuestsForPillarPage();
        }
    };

    useEffect(() => {
        setPersons(persons_adult + persons_0_3 + persons_4_11 + persons_12_17);
    }, [persons_adult, persons_0_3, persons_4_11, persons_12_17]);

    useEffect(() => {
        if (childrenCount > 0 && persons_adult === 0) {
            setPersonsAdult(1);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [childrenCount]);

    useEffect(() => {
        if (!staticPublicResources || !locationPublicResources || !datePublicResources || !guestsPublicResources) {
            getData();
        }
        if (initialLocationData) {
            setLocationURL(initialLocationData.id);
            setSelectedLocation(initialLocationData.value);
            setSelectedLocationLabel(initialLocationData.label);
            setSearchString(initialLocationData.value);
        }
        parseUrlParams();
        const handleResize = () => {
            setIsSmallDevice(window.innerWidth < 992);
            setIsVerySmallDevice(window.innerWidth < 380);
        };
        const handleScroll = () => {
            if (!hasScrolledFromTop && window.scrollY > 0 && isAccordionVisible) {
                setHasScrolledFromTop(true);
            }
        };
        setIsAccordionVisible(isAccordionVisibleProp);
        window.addEventListener('scroll', handleScroll);
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('resize', handleResize);
            if (locationBlurTimeout.current) {
                clearTimeout(locationBlurTimeout.current);
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (allLocations?.length) {
            setSearchedArray(allLocations);
            if (locationURL) {
                const locationData = allLocations.find((location) => location.id === locationURL);
                if (locationData) {
                    setSearchString(locationData.value);
                    setSelectedLocation(locationData.value);
                    setSelectedLocationLabel(locationData.label);
                } else {
                    setLocationURL('');
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allLocations]);

    useEffect(() => {
        if (showModal && !wasModalEverOpen) {
            setWasModalEverOpen(true);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showModal]);

    useEffect(() => {
        setIsAccordionVisible(isAccordionVisibleProp);
    }, [isAccordionVisibleProp]);

    const closeAccordion = () => {
        setIsAccordionVisible(false);
        closeAccordionProp?.();
    };

    useEffect(() => {
        if (!mainSearchFormElement) return undefined;
        const handleSearchDockTouchStart = (event) => {
            if (!event) return;
            event.stopPropagation();
            startingTouchPosition.current = event.touches[0].clientY;
        };
        const handleSearchDockTouchMove = (event) => {
            if (!event) return;
            event.stopPropagation();
            event.preventDefault();
            isTouchMoving.current = true;
            const mainSearchFormHeight = mainSearchFormElement.clientHeight;
            const movedOffset = event.touches[0].clientY - startingTouchPosition.current;
            const percentageMovedTemp = (movedOffset / mainSearchFormHeight) * 100;
            percentageMoved.current = Math.min(100, Math.max(0, percentageMovedTemp));
            mainSearchFormElement.style.transition = '0s';
            mainSearchFormElement.style.transform = `translateY(${percentageMoved.current}%)`;
        };
        const handleSearchDockTouchEnd = (event) => {
            event?.stopPropagation();
            startingTouchPosition.current = null;
            if (!isTouchMoving.current) return;
            mainSearchFormElement.style.transition = '';
            mainSearchFormElement.style.transform = '';
            if (percentageMoved.current > 25) closeAccordion();
            isTouchMoving.current = false;
            percentageMoved.current = null;
        };
        if (isSearchDock && mainSearchFormElement) {
            mainSearchFormElement.addEventListener('touchstart', handleSearchDockTouchStart, { passive: false });
            mainSearchFormElement.addEventListener('touchmove', handleSearchDockTouchMove, { passive: false });
            mainSearchFormElement.addEventListener('touchend', handleSearchDockTouchEnd, { passive: false });
        }

        return () => {
            if (mainSearchFormElement) {
                mainSearchFormElement.removeEventListener('touchstart', handleSearchDockTouchStart, { passive: false });
                mainSearchFormElement.removeEventListener('touchmove', handleSearchDockTouchMove, { passive: false });
                mainSearchFormElement.removeEventListener('touchend', handleSearchDockTouchEnd, { passive: false });
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mainSearchFormElement]);

    const handleGuests = (stateSetterFn, delta, limits, oppositeButtonElement) => {
        stateSetterFn((prev) => {
            const newValue = prev + delta;
            if (newValue === limits.min || newValue === limits.max) {
                oppositeButtonElement.focus();
            }
            return newValue;
        });
    };

    useEffect(() => {
        if (userTouchedPDist) return;
        if (persons >= 6) {
            setPDist(1);
        } else {
            setPDist(0);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [persons]);

    const handlePdistChange = () => {
        if (p_dist === 0) {
            setPDist(1);
        } else {
            setPDist(0);
        }
        setUserTouchedPDist(true);
        guestsInput.current.focus();
    };

    const debouncedFetchLocationData = useRef(
        debounce(
            (newSearchString) => {
                if (!isSearchStringValid(newSearchString)) {
                    fetchLocationsAbortController.current?.abort();
                    setSearchedArray([]);
                    setIsFetchingLocations(false);
                    return;
                }
                fetchLocationsAbortController.current = new AbortController();
                apiPostDataWithConfig(`locations/autocomplete/${langISO}`, { query: newSearchString }, { signal: fetchLocationsAbortController.current.signal })
                    .then((res) => {
                        if (res.data.length > 0) {
                            setSearchedArray(res.data);
                            setFocusedLocationIndex(0);
                        } else {
                            setSearchedArray([]);
                            setFocusedLocationIndex(null);
                        }
                    })
                    .catch((err) => {
                        reportError(err);
                    })
                    .finally(() => {
                        setIsFetchingLocations(false);
                    });
            },
            300,
        ),
        [],
    ).current;

    const handleLocationInputChange = (event) => {
        const newSearchString = event.target.value;
        setSearchString(newSearchString);
        setLocationURL('');
        setSelectedLocation(null);
        setSelectedLocationLabel(null);
        setFocusedLocationIndex(null);

        if (allLocations) {
            const normalizedSearchString = normalizeForSearch(newSearchString);
            setSearchedArray(allLocations.filter((location) => normalizeForSearch(location.value).includes(normalizedSearchString)));
        } else {
            setIsFetchingLocations(isSearchStringValid(newSearchString));
            debouncedFetchLocationData(newSearchString);
        }
    };

    const handleBlurGuests = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setShowGuests(false);
        }
    };

    const handleBlurDateRange = (event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setShowDateRange(false);
        }
    };

    const handleBlurLocationsDesktop = (event) => {
        const { currentTarget, relatedTarget } = event;
        if (locationBlurTimeout.current) {
            clearTimeout(locationBlurTimeout.current);
            locationBlurTimeout.current = null;
        }
        // Postoji timeout na ovoj funckiji jer nije poželjno da se izvrši prije selectLocation funkcije koja se poziva na klik.
        locationBlurTimeout.current = setTimeout(() => {
            if (!currentTarget.contains(relatedTarget)) {
                setShowLocation(false);
            }
        }, 100);
    };

    const handleFocus = (event, stateSetterFn) => {
        event.persist();
        const { currentTarget } = event;
        setShowLocation(false);
        setShowDateRange(false);
        setShowGuests(false);
        stateSetterFn(true);
        setTimeout(() => {
            const scrollOffset = currentTarget.getBoundingClientRect().top + window.scrollY - 150;
            window.scrollTo({ top: scrollOffset, behavior: 'smooth' });
        }, 100);
    };

    useEffect(() => {
        if (showLocation) {
            if (isSmallDevice) {
                setTimeout(() => {
                    locationInputMobileRef.current?.focus();
                }, 100);
            } else {
                locationInput.current?.focus();
            }
        } else if (!isSmallDevice) {
            const { recommendedLocations, withoutLocationLabel } = locationPublicResources;
            if (focusedLocationIndex === null || selectedLocation !== null) return;
            const locationItemId = searchedArray.length > 0 ? searchedArray[focusedLocationIndex].id : recommendedLocations[focusedLocationIndex]?.value ?? '';
            const locationItemText = searchedArray.length > 0 ? searchedArray[focusedLocationIndex].value : recommendedLocations[focusedLocationIndex]?.title ?? withoutLocationLabel;
            const locationItemLabel = searchedArray.length > 0 ? searchedArray[focusedLocationIndex].label : recommendedLocations[focusedLocationIndex]?.title ?? withoutLocationLabel;
            setLocationURL(locationItemId);
            setSelectedLocation(locationItemText);
            setSelectedLocationLabel(locationItemLabel);
            setSearchString(locationItemText);
            setSelectedLocation(locationItemText);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showLocation]);

    const getFlexibleDataForSubmit = () => {
        if (isWishlist) return {};
        return {
            f_length: isFlexible ? f_length : undefined,
            i_d: isFlexible ? undefined : i_d,
        };
    };

    const setDefaultFlexibleSwitchOptions = () => {
        setFlexibleSwitchOptions([
            {
                value: 'exact-dates',
                label: datePublicResources.possibleDeviation.typeTitle,
            },
            {
                value: 'flexible',
                label: datePublicResources.fleksibleInterval.typeTitle,
            },
        ]);
    };

    const getCalendarStartingMonth = () => (startDate
        ? new Date(startDate)
        : set(new Date(), {
            year: datePublicResources.selectedDefaults.year,
            month: datePublicResources.selectedDefaults.month - 1, // Npr. Siječanj je 0.
        }));

    useEffect(() => {
        if (datePublicResources) {
            setCalendarStartingMonth(getCalendarStartingMonth());
            setDefaultFlexibleSwitchOptions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datePublicResources]);

    const selectLocation = (locationId, locationText, locationLabel, locationIndex = null) => {
        if (locationBlurTimeout.current) {
            clearTimeout(locationBlurTimeout.current);
            locationBlurTimeout.current = null;
        }
        if (isFetchingLocations) return;
        const isFacilityOrUnit = isLocationAnId(locationText);
        fetchLocationsAbortController.current?.abort();
        const updateSelectedLocation = () => {
            setLocationURL(locationId);
            setSelectedLocation(locationText);
            setSelectedLocationLabel(locationLabel);
            setSearchString(locationText);
            setFocusedLocationIndex(locationIndex);
            setShowModal((prev) => (isFacilityOrUnit ? false : prev));
            setShowLocation(false);
            setShowDateRange(!isFacilityOrUnit);
        };
        if (showModal) {
            // INP: jer u mobilnom modalu prelazak na korak s datumima montira vertikalni kalendar sa svim mjesecima.
            runAfterNextPaint(updateSelectedLocation);
        } else {
            // Desktop ostaje sinkron zbog fokusa na input datuma ispod.
            updateSelectedLocation();
        }
        if (!isFacilityOrUnit) {
            inputDateRangeRef.current.focus();
        }
    };

    const handleLocationInputKeyDown = (event) => {
        const { recommendedLocations, withoutLocationLabel } = locationPublicResources;
        if (isFetchingLocations) return;
        const locationsCount = allLocations || searchedArray.length > 0 ? searchedArray.length : recommendedLocations.length + 1;
        if (locationsCount < 1) return;
        const lastLocationIndex = locationsCount - 1;
        let nextIndex = null;
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (focusedLocationIndex === null) {
                nextIndex = 0;
                setFocusedLocationIndex(nextIndex);
            } else {
                nextIndex = focusedLocationIndex + 1;
                nextIndex = nextIndex > lastLocationIndex ? null : nextIndex;
                setFocusedLocationIndex(nextIndex);
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (focusedLocationIndex === null) {
                nextIndex = lastLocationIndex;
                setFocusedLocationIndex(lastLocationIndex);
            } else {
                nextIndex = focusedLocationIndex - 1;
                nextIndex = nextIndex < 0 ? null : nextIndex;
                setFocusedLocationIndex(nextIndex);
            }
        } else if (event.key === 'Enter') {
            if (focusedLocationIndex === null) return;
            if (searchedArray.length > 0) {
                const location = searchedArray[focusedLocationIndex];
                selectLocation(location.id, location.value, location.label, focusedLocationIndex);
            } else {
                const location = recommendedLocations[focusedLocationIndex];
                const locationTitle = location?.title ?? withoutLocationLabel;
                selectLocation(location?.value ?? '', locationTitle, locationTitle, focusedLocationIndex);
            }
        }
        if (nextIndex !== null) {
            const locationItemId = searchedArray.length > 0 ? searchedArray[nextIndex].id : recommendedLocations[nextIndex]?.value ?? '';
            const locationElement = locationDropdownDesktopRef.current?.querySelector(`[data-url="${locationItemId}"]`);
            locationElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
        }
    };

    const getSelectedGuestsDisplayText = (splitStr) => {
        if (persons === 0) return guestsPublicResources?.chooseLabel;
        const total_kids = persons_0_3 + persons_4_11 + persons_12_17;
        if (total_kids > 0) {
            const fullText = guestsPublicResources.selectedAdultsWithKidsTemplate
                .replace('{{TOTAL_PERSONS_COUNT}}', persons)
                .replace('{{ADULTS_COUNT}}', persons_adult)
                .replace('{{KIDS_COUNT}}', total_kids);
            if (splitStr) {
                const totalPersonsStr = String(persons);
                const splitIndex = fullText.indexOf(totalPersonsStr) + totalPersonsStr.length;
                return (
                    <>
                        {fullText.slice(0, splitIndex)}
                        <br />
                        {fullText.slice(splitIndex).trim()}
                    </>
                );
            }
            return fullText;
        }
        return guestsPublicResources.selectedOnlyAdultsTemplate
            .replace('{{TOTAL_PERSONS_COUNT}}', persons)
            .replace('{{ADULTS_COUNT}}', persons_adult);
    };

    const openLocationModal = () => {
        runAfterNextPaint(() => {
            setShowModal(true);
            setShowLocation(true);
            setShowDateRange(false);
            setShowGuests(false);
            setSavedLocationData({
                locationURL,
                searchString,
                selectedLocation,
                selectedLocationLabel,
            });
        });
    };

    const openGuestsModal = () => {
        runAfterNextPaint(() => {
            setShowModal(true);
            setShowLocation(false);
            setShowDateRange(false);
            setShowGuests(true);
        });
    };

    const openDatesModal = () => {
        runAfterNextPaint(() => {
            setShowModal(true);
            setShowLocation(false);
            setShowDateRange(true);
            setShowGuests(false);
        });
    };

    const closeModal = () => {
        const newCalendarStartingMonth = getCalendarStartingMonth();
        setShowModal(false);
        setShowLocation(false);
        setShowDateRange(false);
        setShowGuests(false);
        setCalendarStartingMonth(newCalendarStartingMonth);
    };

    const handleModalCancel = () => {
        if (showLocation) {
            closeModal();
            setLocationURL(savedLocationData?.locationURL ?? '');
            setSelectedLocation(savedLocationData?.selectedLocation ?? null);
            setSelectedLocationLabel(savedLocationData?.selectedLocationLabel ?? null);
            setSearchString(savedLocationData?.searchString ?? '');
        } else if (showDateRange) {
            setStartDate(null);
            setEndDate(null);
            setFLength(0);
            setIntervalDeviation(0);
            setSelectedDatesMemo({
                startDate: null,
                endDate: null,
            });
        } else if (showGuests) {
            setPersons(guestsPublicResources.adults.minimumNumber);
            setPersonsAdult(guestsPublicResources.adults.minimumNumber);
            setPersons12_17(0);
            setPersons4_11(0);
            setPersons0_3(0);
            setPDist(0);
        }
    };

    const getInputGuestsPlaceholder = () => {
        if (!isSearchDock) return null;
        return persons > guestsPublicResources.adults.minimumNumber
            ? ` ${guestsPublicResources.selectedNumberOfGuestsLabel} ${persons}`
            : staticPublicResources.desktop.numberOfGuestsLabel;
    };

    const getDateRangePickerErrorMessage = () => {
        if (shouldShowLessThanMinOvernightStaysError) {
            return datePublicResources.fleksibleInterval.selectIntervalMinimumLengthErrorMessageTemplate.replace('{{NUMBER}}', f_length);
        }
        if ((startDate && !endDate) || shouldShowMoreThanMaxOvernightStaysError) {
            return datePublicResources.fleksibleInterval.selectIntervalMaximumLengthErrorMessage;
        }
        if (!startDate) return datePublicResources.fleksibleInterval.selectIntervalErrorMessage;
        if (endDate && !f_length) return datePublicResources.fleksibleInterval.selectIntervalLengthErrorMessage;
        return null;
    };

    const getDateRangePickerHintMessage = (startDateFormatted, endDateFormatted, isModal) => {
        const intervalStringified = `${startDateFormatted} - ${endDateFormatted}`;
        if (!isFlexible) return isModal && endDate ? intervalStringified : staticPublicResources?.desktop?.datePickLongLabel;
        if ((!startDate || !endDate) && !f_length) return datePublicResources.fleksibleInterval.selectLengthAndIntervalMessage;
        let messageOvernightStays = '';
        let messageInterval = '';
        if (startDate && endDate) {
            messageInterval = `${datePublicResources.fleksibleInterval.interval}: ${intervalStringified}`;
        } else {
            messageInterval = datePublicResources.fleksibleInterval.selectIntervalErrorMessage;
        }
        if (f_length) {
            messageOvernightStays = `${datePublicResources.fleksibleInterval.intervalLengthMessage}: ${f_length}`;
        } else {
            messageOvernightStays = datePublicResources.fleksibleInterval.intervalLength;
        }
        return `${messageOvernightStays} - ${messageInterval}`;
    };

    const getMobileSearchModalSubmitButtonText = () => {
        if (isLocationAnId(selectedLocation)) return staticPublicResources.common.searchLabel;
        if (showLocation) return staticPublicResources.desktop.datePickLongLabel;
        if (showDateRange) return datePublicResources.nextStepButtonText;
        return staticPublicResources.common.searchLabel;
    };

    const getMobileSearchModalSecondaryButtonText = () => {
        if (showLocation) return locationPublicResources.cancelLabel;
        return datePublicResources.cancelLabel;
    };

    const handleFlexibleSwitch = (newSelectedIndex) => {
        runAfterNextPaint(() => {
            const newValue = flexibleSwitchOptions[newSelectedIndex].value;
            setIsFlexible(newValue === 'flexible');
            setStartDate(selectedDatesMemo.startDate);
            setEndDate(selectedDatesMemo.endDate);
            setSelectedDatesMemo({
                startDate,
                endDate,
            });
        });
        dispatchTrackingEvent(QUICK_SEARCH_DATE_MODE_SELECTED);
    };

    const onClickDayLessThanMinOvernightStays = () => {
        setShouldShowLessThanMinOvernightStaysError(true);
        setShouldShowMoreThanMaxOvernightStaysError(false);
    };

    const onClickDayMoreThanMaxOvernightStays = () => {
        setShouldShowMoreThanMaxOvernightStaysError(true);
        setShouldShowLessThanMinOvernightStaysError(false);
    };

    const shouldDisableSubmitSearch = () => (searchString && !selectedLocation)
        || (isFlexible && ((endDate && !f_length) || (f_length && !endDate)));

    const prepareParamsForSubmit = () => ({
        location_id: locationURL,
        selectedLocation,
        p_dist,
        persons_adult,
        persons_12_17,
        persons_4_11,
        persons_0_3,
        from: formatDateForRequest(startDate),
        to: formatDateForRequest(endDate),
        ...getFlexibleDataForSubmit(),
    });

    const submitSearch = (trackingEvents = []) => {
        const params = prepareParamsForSubmit();
        const storedSearchFromLS = localStorage?.getItem(ADRIATIC_LAST_SEARCH_PARAMS);
        // Mora biti prije "saveSearchParams(params)" zbog toga nije unutar provjere "!IS_FRAME_SERVICE" na dnu bloka.
        if (isSearchResults && storedSearchFromLS && !IS_FRAME_SERVICE) {
            const parsedSearch = JSON.parse(storedSearchFromLS);
            const storedParams = parsedSearch?.params;
            const hasStoredDatesLS = (storedParams?.from || storedParams?.to);
            const hasStoredDatesState = (params?.from || params?.to);
            if (hasStoredDatesState && hasStoredDatesLS) {
                const datesAreSame = (
                    params.from === storedParams.from
                    && params.to === storedParams.to
                );
                if (datesAreSame) {
                    dispatchTrackingEvent(MANUAL_INPUT_DATES_FROM_LOCAL_STORAGE);
                } else {
                    dispatchTrackingEvent(MANUAL_INPUT_DATES_OUTSIDE_LOCAL_STORAGE);
                }
            }
        }
        if (!isLocationAnId(searchString) && !isSearchStringAllDigits(searchString)) {
            saveSearchParams(params);
        }
        // INP: jer update na stranici rezultata re-renderira cijelu stranicu (sve kartice) prije preusmjeravanja.
        runAfterNextPaint(() => update(params, true));
        if (!IS_FRAME_SERVICE && !isWishlist) {
            if (isLocationAnId(selectedLocation)) {
                const isUnit = selectedLocation.includes('-');
                trackingEvents.push({ name: isUnit ? QUICK_SEARCH_LOCATION_UNIT_SELECTED : QUICK_SEARCH_LOCATION_PROPERTY_SELECTED });
            }
            trackingEvents.forEach(({ name, payload }) => {
                if (payload) {
                    dispatchTrackingEvent(name, payload);
                } else {
                    dispatchTrackingEvent(name);
                }
            });
        }
    };

    const submitSearchModalButtonClick = () => {
        if (isLocationAnId(selectedLocation)) {
            submitSearch();
            return;
        }
        if (showLocation) {
            runAfterNextPaint(() => {
                setShowLocation(false);
                setShowDateRange(true);
            });
            return;
        }
        if (showDateRange) {
            runAfterNextPaint(() => {
                setShowDateRange(false);
                setShowGuests(true);
            });
            return;
        }
        let trackingEventName = SEARCH_RESULTS_QUICK_SEARCH_SUBMIT_DETAILED;
        if (isHome) {
            trackingEventName = HOMEPAGE_SEARCH_QUICK_LINK_CLICK_DETAILED;
        } else if (isPillarPage) {
            trackingEventName = PILLAR_PAGE_SEARCH_QUICK_LINK_CLICK_DETAILED;
        }
        submitSearch([{ name: trackingEventName }]);
    };

    const handleFlexibleDaysMinusButton = () => {
        const newFLength = Math.max(0, f_length - 1);
        setFLength(newFLength);
        if (newFLength <= 0) {
            flexibleDaysPlusButton.current?.focus();
        }
    };

    const shouldDisableFlexibleDaysPlusButton = (newFLength) => newFLength >= datePublicResources.fleksibleInterval.maximalLengthOfStay
        || newFLength >= differenceInDays(endDate, startDate);

    const handleFlexibleDaysPlusButton = () => {
        const newFLength = Math.min(datePublicResources.fleksibleInterval.maximalIntervalLength, f_length + 1);
        setFLength(newFLength);
        if (shouldDisableFlexibleDaysPlusButton(newFLength)) {
            flexibleDaysMinusButton.current?.focus();
        }
    };

    const toggleAccordion = () => {
        setIsAccordionOpen(!isAccordionOpen);
    };

    const isSelectedLocationInRecommendedLocations = () => {
        const { recommendedLocations, withoutLocationLabel } = locationPublicResources;
        if (!recommendedLocations) { return false; }
        if (selectedLocation === withoutLocationLabel) { return true; }
        return recommendedLocations.some((location) => location.title === selectedLocation);
    };

    const orientation = isSmallDevice ? VERTICAL_ORIENTATION : HORIZONTAL_ORIENTATION;
    const startDateFormatted = formatLocalizedDate(
        startDate,
        langISOForDateFormats,
        { shortYear: true },
    );
    const endDateFormatted = formatLocalizedDate(
        endDate,
        langISOForDateFormats,
        { shortYear: true },
    );
    const renderFormattedInterval = (
        <span>
            <span>{startDateFormatted}</span>
            <span> - </span>
            <span>{endDateFormatted}</span>
            {!isFlexible && i_d > 0 && (
                <span> (
                    <span className="formatted-interval__interval-deviation">
                        <span className="formatted-interval__plus-minus">
                            <svg xmlns="http://www.w3.org/2000/svg" width="5" height="5" viewBox="0 0 5 5" fill="none">
                                <path d="M3 2H5V3H3V5H2V3H0V2H2V0H3V2Z" fill="#7D7D7D" />
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" width="5" height="1" viewBox="0 0 5 1" fill="none">
                                <path d="M5 0V1H0V0H5Z" fill="#7D7D7D" />
                            </svg>
                        </span>{i_d}
                    </span>)
                </span>
            )}
            {isFlexible && (
                <span> ({datePublicResources?.overnightsText}: {f_length})</span>
            )}
        </span>
    );
    const renderDatePickerFlexibleDaysInput = datePublicResources && (
        <div className="date-range-picker__flexible-days-input">
            <p className="date-range-picker__flexible-days-input-title">
                {datePublicResources.lengthOfStayLabel}
            </p>
            <div className="date-range-picker__flexible-days-input-right-content">
                <p className={`date-range-picker__flexible-days-input-text ${f_length <= 0 ? 'date-range-picker__flexible-days-input-text--zero' : ''}`}>
                    {f_length > 0
                        ? `${datePublicResources.fleksibleInterval.intervalLengthMessage}: ${f_length}`
                        : datePublicResources.fleksibleInterval.intervalLength}
                </p>
                <button
                    ref={flexibleDaysMinusButton}
                    type="button"
                    className="date-range-picker__flexible-days-input-button"
                    onClick={handleFlexibleDaysMinusButton}
                    disabled={f_length <= 0}
                >
                    <MdRemove className="date-range-picker__flexible-days-input-button-icon" />
                </button>
                <button
                    ref={flexibleDaysPlusButton}
                    type="button"
                    className="date-range-picker__flexible-days-input-button"
                    onClick={handleFlexibleDaysPlusButton}
                    disabled={shouldDisableFlexibleDaysPlusButton(f_length)}
                >
                    <MdAdd className="date-range-picker__flexible-days-input-button-icon" />
                </button>
            </div>
        </div>
    );
    const renderDatePickerFlexibleSwitch = flexibleSwitchOptions && (
        <SegmentedControl
            className="date-range-picker__flexible-switch-container"
            selectedIndex={isFlexible ? 1 : 0}
            options={flexibleSwitchOptions}
            onChange={handleFlexibleSwitch}
        />
    );
    const renderDatePickerTopContent = staticPublicResources && (
        <div className="date-range-picker__header-outer">
            <div className="date-range-picker__header">
                <div className="date-range-picker__header-text-container">
                    {isHomeForFrame && (
                        <span className="date-range-picker__header-home-frame-title">
                            {staticPublicResources.mobile.datePickLabel}
                        </span>
                    )}
                    <span className="hint-message">
                        {getDateRangePickerHintMessage(startDateFormatted, endDateFormatted, false)}
                    </span>
                </div>
                {!isWishlist && renderDatePickerFlexibleSwitch}
            </div>
            {!isHomeForFrame && isFlexible && renderDatePickerFlexibleDaysInput}
        </div>
    );
    const renderDatePickerLegend = datePublicResources && (
        <div className="date-range-picker__legend-container">
            <span className="date-range-picker__legend-item">
                <span className="date-range-picker__legend-icon date-range-picker__legend-icon--edges" />
                <span className="date-range-picker__legend-text">
                    {datePublicResources.intervalColor.startAndEndDate}
                </span>
            </span>
            <span className="date-range-picker__legend-item">
                <span className="date-range-picker__legend-icon date-range-picker__legend-icon--middle" />
                <span className="date-range-picker__legend-text">
                    {datePublicResources.intervalColor.middle}
                </span>
            </span>
        </div>
    );
    const dateRangePickerErrorMessage = datePublicResources && getDateRangePickerErrorMessage();
    const isInfoMessage = startDate
        && !endDate
        && !shouldShowLessThanMinOvernightStaysError
        && !shouldShowMoreThanMaxOvernightStaysError;
    const renderDatePickerErrorMessage = (
        dateRangePickerErrorMessage && (
            <div className="date-range-picker__bottom-error">
                <MdError className={`date-range-picker__bottom-error-icon ${isInfoMessage ? 'date-range-picker__bottom-error-icon--grey' : 'date-range-picker__bottom-error-icon--red'}`} />
                <p className={`date-range-picker__bottom-error-text ${isInfoMessage ? 'date-range-picker__bottom-error-text--info' : ''}`}>
                    {dateRangePickerErrorMessage}
                </p>
            </div>
        )
    );
    const renderDatePickerDeviationDaysOptions = datePublicResources && (
        <div className="date-range-picker__exact-date-options">
            {datePublicResources.possibleDeviation.intervalDeviationOptions.map((option) => (
                <button
                    key={`exact-day-option-${option.value}`}
                    type="button"
                    className={`date-range-picker__exact-date-option ${i_d === Number(option.value) ? 'date-range-picker__exact-date-option--selected' : ''}`}
                    onClick={() => setIntervalDeviation(Number(option.value))}
                >
                    {option.title}
                </button>
            ))}
        </div>
    );
    const renderDatePickerBottomContent = (
        <div className="date-range-picker__bottom-content">
            {renderDatePickerLegend}
            {!isWishlist && (isFlexible ? renderDatePickerErrorMessage : renderDatePickerDeviationDaysOptions)}
            {isHomeForFrame && isFlexible && renderDatePickerFlexibleDaysInput}
        </div>
    );
    const guestsBlock = (device) => (
        guestsPublicResources && (
            <div className="guestsAdults--wrapper">
                <div className="guestsAdults">
                    <div className="guestsLeft">
                        <p className="guestsLeft-p">{guestsPublicResources.adults.label}</p>
                        <span className="guestsLeft-span">{guestsPublicResources.adults.description}</span>
                    </div>
                    <div className="guestsRight">
                        <button
                            ref={adultsLeftButton}
                            type="button"
                            className="guestsRight-btn leftButton"
                            onClick={() => handleGuests(setPersonsAdult, -1, { min: minAdults, max: guestsPublicResources.adults.maximumNumber }, adultsRightButton.current)}
                            disabled={persons_adult === minAdults}
                        >
                            -
                        </button>
                        <p className="guestsRight-p">{persons_adult}</p>
                        <button
                            ref={adultsRightButton}
                            type="button"
                            className="guestsRight-btn rightButton"
                            onClick={() => handleGuests(setPersonsAdult, 1, { min: minAdults, max: guestsPublicResources.adults.maximumNumber }, adultsLeftButton.current)}
                            disabled={persons_adult >= guestsPublicResources.adults.maximumNumber}
                        >
                            +
                        </button>
                    </div>
                    <hr className="guests-separator" />
                </div>
                <div className="guestsAdults">
                    <div className="guestsLeft">
                        <p className="guestsLeft-p">{guestsPublicResources.children12to17.label}</p>
                        <span className="guestsLeft-span">{guestsPublicResources.children12to17.description}</span>
                    </div>
                    <div className="guestsRight">
                        <button
                            ref={children12to17LeftButton}
                            type="button"
                            className="guestsRight-btn leftButton"
                            onClick={() => handleGuests(setPersons12_17, -1, { min: 0, max: guestsPublicResources.children12to17.maximumNumber }, children12to17RightButton.current)}
                            disabled={persons_12_17 === 0}
                        >
                            -
                        </button>
                        <p className="guestsRight-p">{persons_12_17}</p>
                        <button
                            ref={children12to17RightButton}
                            type="button"
                            className="guestsRight-btn rightButton"
                            onClick={() => handleGuests(setPersons12_17, 1, { min: 0, max: guestsPublicResources.children12to17.maximumNumber }, children12to17LeftButton.current)}
                            disabled={persons_12_17 >= guestsPublicResources.children12to17.maximumNumber}
                        >
                            +
                        </button>
                    </div>
                    <hr className="guests-separator" />
                </div>
                <div className="guestsAdults">
                    <div className="guestsLeft">
                        <p className="guestsLeft-p">{guestsPublicResources.children4to11.label}</p>
                        <span className="guestsLeft-span">{guestsPublicResources.children4to11.description}</span>
                    </div>
                    <div className="guestsRight">
                        <button
                            ref={children4to11LeftButton}
                            type="button"
                            className="guestsRight-btn leftButton"
                            onClick={() => handleGuests(setPersons4_11, -1, { min: 0, max: guestsPublicResources.children4to11.maximumNumber }, children4to11RightButton.current)}
                            disabled={persons_4_11 === 0}
                        >
                            -
                        </button>
                        <p className="guestsRight-p">{persons_4_11}</p>
                        <button
                            ref={children4to11RightButton}
                            type="button"
                            className="guestsRight-btn rightButton"
                            onClick={() => handleGuests(setPersons4_11, 1, { min: 0, max: guestsPublicResources.children4to11.maximumNumber }, children4to11LeftButton.current)}
                            disabled={persons_4_11 >= guestsPublicResources.children4to11.maximumNumber}
                        >
                            +
                        </button>
                    </div>
                    <hr className="guests-separator" />
                </div>
                <div className="guestsAdults">
                    <div className="guestsLeft">
                        <p className="guestsLeft-p">{guestsPublicResources.children0to3.label}</p>
                        <span className="guestsLeft-span">{guestsPublicResources.children0to3.description}</span>
                    </div>
                    <div className="guestsRight">
                        <button
                            ref={children0to3LeftButton}
                            type="button"
                            className="guestsRight-btn leftButton"
                            onClick={() => handleGuests(setPersons0_3, -1, { min: 0, max: guestsPublicResources.children0to3.maximumNumber }, children0to3RightButton.current)}
                            disabled={persons_0_3 === 0}
                        >
                            -
                        </button>
                        <p className="guestsRight-p">{persons_0_3}</p>
                        <button
                            ref={children0to3RightButton}
                            type="button"
                            className="guestsRight-btn rightButton"
                            onClick={() => handleGuests(setPersons0_3, 1, { min: 0, max: guestsPublicResources.children0to3.maximumNumber }, children0to3LeftButton.current)}
                            disabled={persons_0_3 >= guestsPublicResources.children0to3.maximumNumber}
                        >
                            +
                        </button>
                    </div>
                    <hr className="guests-separator" />
                </div>
                <div className="guestsAdults">
                    <div className="guestsLeft">
                        <p className="guestsLeft-p">{guestsPublicResources.inSeveralAccommodation.label}</p>
                    </div>
                    <div className="guestsRight guestsRight--multipleUnits">
                        <div className="custom-control custom-switch">
                            <input
                                type="checkbox"
                                className="custom-control-input"
                                id={`customSwitch2-${device}`}
                                name="p_dist"
                                value={p_dist}
                                checked={p_dist}
                                onChange={handlePdistChange}
                            />
                            <label
                                className="custom-control-label"
                                htmlFor={`customSwitch2-${device}`}
                            >
                                <span className="sr-only">
                                    {guestsPublicResources.inSeveralAccommodation.label}
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        )
    );
    const getLocationsDropdownTitle = () => (isLocationAnId(searchString) ? locationPublicResources.locationsLabelResultFacilityOrUnitString : locationPublicResources.locationsLabelResultLocationString);
    const locationBlock = (
        <>
            {isFetchingLocations && (
                <>
                    <p className="location-dropdown__recommended-label">{getLocationsDropdownTitle()}</p>
                    <div className="locations-wrap">
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                        <Skeleton />
                    </div>
                </>
            )}
            {!isFetchingLocations && (allLocations || (searchString && searchedArray.length > 0)) ? (
                <>
                    <p className="location-dropdown__recommended-label">{getLocationsDropdownTitle()}</p>
                    {!searchedArray.length && (
                        <div className="locationDropdown__info-box">
                            <MdInfoOutline className="locationDropdown-svg locationDropdown__info-box-icon" />
                            <p className="locationDropdown__info-box-text">{locationPublicResources.unrecognizedLocationLabel}</p>
                        </div>
                    )}
                    <div className="locations-wrap">
                        {searchedArray.map((item, i) => (
                            <button
                                key={i}
                                onClick={() => selectLocation(item.id, item.value, item.label, i)}
                                type="button"
                                data-value={item.value}
                                data-url={item.id}
                                aria-label="Select Location"
                                className={`locations-wrap-btn location-dropdown__item ${locationURL === item.id ? 'location-dropdown__item--selected' : ''} ${focusedLocationIndex === i ? 'location-dropdown__item--focused' : ''}`}
                            >
                                {isLocationAnId(searchString)
                                    ? <MdApartment className="locationDropdown-svg locations-wrap-btn-svg location-dropdown__item-icon" />
                                    : <MdOutlinePlace className="locationDropdown-svg locations-wrap-btn-svg location-dropdown__item-icon" />}
                                <span className="location-dropdown__item-text" dangerouslySetInnerHTML={{ __html: `${item.label}` }} />
                            </button>
                        ))}
                    </div>
                </>
            ) : (
                !isFetchingLocations && (
                    <>
                        {isSearchStringValid(searchString) ? (
                            !isSelectedLocationInRecommendedLocations() && (
                                <div className="locationDropdown__info-box">
                                    <MdInfoOutline className="locationDropdown-svg locationDropdown__info-box-icon" />
                                    <p className="locationDropdown__info-box-text">{locationPublicResources.unrecognizedLocationLabel}</p>
                                </div>
                            )
                        ) : (
                            <div className="locationDropdown__info-box">
                                <MdInfoOutline className="locationDropdown-svg locationDropdown__info-box-icon" />
                                <p className="locationDropdown__info-box-text">{locationPublicResources.inputUsageInstructions}</p>
                            </div>
                        )}
                        <p className="location-dropdown__recommended-label">{locationPublicResources.recommendedLabel}</p>
                        {locationPublicResources && (
                            <div className="locations-wrap">
                                {locationPublicResources.recommendedLocations.map((item, i) => (
                                    <button
                                        key={i}
                                        onClick={() => {
                                            selectLocation(item.value, item.title, item.title, i);
                                            if (!IS_FRAME_SERVICE) {
                                                dispatchTrackingEvent(
                                                    QUICK_SEARCH_SUGGESTED_LOCATION_SELECTED,
                                                    { location: item.title },
                                                );
                                            }
                                        }}
                                        type="button"
                                        data-value={item.title}
                                        data-url={item.value}
                                        className={`locations-wrap-btn location-dropdown__item ${locationURL === item.value ? 'location-dropdown__item--selected' : ''} ${focusedLocationIndex === i ? 'location-dropdown__item--focused' : ''}`}
                                    >
                                        <MdOutlinePlace className="locationDropdown-svg locations-wrap-btn-svg location-dropdown__item-icon" />
                                        {item.title}
                                    </button>
                                ))}
                                <button
                                    className={`locations-wrap-btn location-dropdown__item location-dropdown__item--entire-croatia ${selectedLocation === locationPublicResources.withoutLocationLabel ? 'location-dropdown__item--selected' : ''} ${focusedLocationIndex === locationPublicResources.recommendedLocations.length ? 'location-dropdown__item--focused' : ''}`}
                                    type="button"
                                    data-value={locationPublicResources.withoutLocationLabel}
                                    data-url=""
                                    onClick={() => selectLocation('', locationPublicResources.withoutLocationLabel, locationPublicResources.withoutLocationLabel, locationPublicResources.recommendedLocations.length)}
                                >
                                    <MdOutlineMyLocation className="locationDropdown-svg locations-wrap-btn-svg location-dropdown__item-icon" />
                                    {locationPublicResources.withoutLocationLabel}
                                </button>
                            </div>
                        )}
                    </>
                )
            )}
        </>
    );
    const minDate = datePublicResources?.intervalFromDate ? parseISO(datePublicResources.intervalFromDate) : null;
    const maxDate = datePublicResources?.intervalFromDate ? parseISO(datePublicResources.intervalToDate) : null;
    const isHomeOrSearchResultsOrWishlistOrPillarPage = isHome || isHomeForFrame || isWishlist || isSearchResults || isPillarPage;
    const isInputLocationTypeHidden = isHomeOrSearchResultsOrWishlistOrPillarPage && !showLocation;
    const mobileSearchFormDivClasses = isSearchResults
        ? `main-search-form-accordion__items d-lg-none ${isAccordionOpen ? '' : 'collapsed'}  ${isAccordionVisible || isSearchDock ? '' : 'closeSearchDisplay'}`
        : 'main-search-form-accordion__items d-lg-none';
    const mobileSearchFormCloseButtonDivClasses = isSearchResults
        ? `main-search-form-accordion-button d-lg-none ${!isAccordionOpen ? '' : 'collapsed'}`
        : 'main-search-form-accordion-button collapsed d-lg-none';

    return (
        <>
            {isLoading && searchFormLoader}
            {staticPublicResources && (
                <>
                    {wasModalEverOpen && (
                        <ModalControl
                            id={isSearchDock ? QUICK_SEARCH_MODAL_DOCK_ID : QUICK_SEARCH_MODAL_ID}
                            isOpen={showModal}
                            disableBodyScroll={true}
                            onClose={closeModal}
                            dependentState={isSearchDock && isAccordionVisible}
                            desktopWidth="0"
                            desktopHeight="0"
                            mobileHeight={`${isVerySmallDevice ? 100 : 90}%`}
                            animate={true}
                            mobileSwipeToClose={true}
                            mobileSwipeOnlyOnHeader={true}
                            hideOnDesktop={true}
                        >
                            <div className="mobile-search-modal" translate="no">
                                <div className="content">
                                    <div className="header modal-control-header">
                                        <h2 className="header-h2">{staticPublicResources.mobile.formTitle}</h2>
                                        <button
                                            type="button"
                                            className="close-quick-search-modal"
                                            onClick={closeModal}
                                            aria-label="Close"
                                        >
                                            <MdClose className="close-quick-search-modal__icon" />
                                        </button>
                                    </div>
                                    <div className="content-inner">
                                        {showLocation && (
                                            <div className="content-inner-wrap location-wrap">
                                                <div className="mobile-search-modal__header">
                                                    <h3 className="mobile-search-modal__title">
                                                        {staticPublicResources.mobile.locationLabel}
                                                    </h3>
                                                </div>
                                                <div className="location-modal fillModal">
                                                    <div className="locationDropdown">
                                                        <div className="inputLocationOuter">
                                                            <MdOutlinePlace className="locationDropdown-svga input-location__icon" />
                                                            <input
                                                                ref={locationInputMobileRef}
                                                                autoComplete="off"
                                                                className={`inputLocation ${searchString && !selectedLocation ? 'inputLocation--error' : ''}`}
                                                                name="location"
                                                                type="text"
                                                                placeholder={staticPublicResources.desktop.locationLabel}
                                                                value={searchString || ''}
                                                                onChange={handleLocationInputChange}
                                                                onKeyDown={handleLocationInputKeyDown}
                                                            />
                                                            {searchString && !selectedLocation && (
                                                                <p className="locationDropdown-p location-error-message">
                                                                    {locationPublicResources.incorrectLocationLabelErrorString}
                                                                </p>
                                                            )}
                                                            {searchString && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        setSearchString('');
                                                                        setSelectedLocation(null);
                                                                        setSelectedLocationLabel(null);
                                                                        setLocationURL('');
                                                                        setSearchedArray(allLocations ?? []);
                                                                        locationInputMobileRef.current.focus();
                                                                    }}
                                                                    aria-label="Clear Locations"
                                                                    className="date-range-picker-input__clear-dates"
                                                                >
                                                                    <IoMdCloseCircleOutline className="date-range-picker-input__clear-dates-svg" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="locations-container">
                                                            {locationBlock}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        {showDateRange && (
                                            <div className="date-range-outer">
                                                <div ref={dateRangeWrapRef} className="content-inner-wrap date-range-wrap">
                                                    <div className="mobile-search-modal__header">
                                                        <h3 className="mobile-search-modal__title">
                                                            {staticPublicResources.mobile.datePickLabel}
                                                        </h3>
                                                        <span className="hint-message">
                                                            {getDateRangePickerHintMessage(startDateFormatted, endDateFormatted, true)}
                                                        </span>
                                                    </div>
                                                    {!isWishlist && renderDatePickerFlexibleSwitch}
                                                    {isFlexible && renderDatePickerFlexibleDaysInput}
                                                    {renderDatePickerLegend}
                                                    <div className="date-range-modal fillModal">
                                                        <DateRangePicker
                                                            isDatePickerShown={showDateRange}
                                                            initialMonth={calendarStartingMonth}
                                                            startDate={startDate}
                                                            endDate={endDate}
                                                            onDatesChange={(newStartDate, newEndDate) => {
                                                                setStartDate(newStartDate);
                                                                setEndDate(newEndDate);
                                                                setShouldShowLessThanMinOvernightStaysError(false);
                                                                setShouldShowMoreThanMaxOvernightStaysError(false);
                                                            }}
                                                            minDate={minDate}
                                                            maxDate={maxDate}
                                                            orientation={orientation}
                                                            notAllowedToSelectMessage={datePublicResources.notAllowedToSelectMessage}
                                                            ssrParameters={ssrParameters}
                                                            minOvernightStays={isFlexible ? f_length : null}
                                                            maxOvernightStays={isFlexible
                                                                ? datePublicResources.fleksibleInterval.maximalIntervalLength
                                                                : null}
                                                            onClickDayLessThanMinOvernightStays={onClickDayLessThanMinOvernightStays}
                                                            onClickDayMoreThanMaxOvernightStays={onClickDayMoreThanMaxOvernightStays}
                                                        />
                                                    </div>
                                                    {!isWishlist && !isFlexible && (
                                                        <div className="date-range-picker__exact-date-options-mobile-container">
                                                            {renderDatePickerDeviationDaysOptions}
                                                        </div>
                                                    )}
                                                </div>
                                                {isFlexible && renderDatePickerErrorMessage}
                                            </div>
                                        )}
                                        {showGuests && (
                                            <div className="content-inner-wrap guests-wrap">
                                                <div className="mobile-search-modal__header">
                                                    <h3 className="mobile-search-modal__title">{staticPublicResources.mobile.numberOfGuestsLabel}</h3>
                                                    <span className="hint-message">
                                                        {persons > guestsPublicResources.adults.minimumNumber
                                                            ? getSelectedGuestsDisplayText()
                                                            : guestsPublicResources.chooseLabel}
                                                    </span>
                                                </div>
                                                <div className="guests-modal fillModal">
                                                    <div className="guestsDropdown">
                                                        {guestsBlock('desktop')}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div className="mobile-search-modal__footer">
                                        <button className="mobile-search-modal__cancel-button adr-btn-tertiary-md" type="button" onClick={handleModalCancel}>
                                            {getMobileSearchModalSecondaryButtonText()}
                                        </button>
                                        <button
                                            className="mobile-search-modal__submit-button adr-btn-primary-md"
                                            type="button"
                                            disabled={shouldDisableSubmitSearch()}
                                            onClick={submitSearchModalButtonClick}
                                        >
                                            {getMobileSearchModalSubmitButtonText()}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </ModalControl>
                    )}
                    <div className={`searchFormWrap ${showModal ? 'zLow' : ''}`} id="searchFormWrap">
                        <form
                            ref={(el) => {
                                if (mainSearchFormElement !== el) {
                                    setMainSearchFormElement(el);
                                }
                            }}
                            className={`mainSearchForm ${!isAccordionVisible && isSearchDock ? 'main-search-form--mobile-collapsed' : ''}`}
                            translate="no"
                        >
                            <div className={`main-search-form__caret ${showLocation ? 'caret--location-dropdown' : ''} ${showDateRange ? 'caret--date-range-picker-dropdown' : ''} ${showGuests ? 'caret--guests-dropdown' : ''}`}>
                                <svg className="main-search-form__caret-svg" xmlns="http://www.w3.org/2000/svg" width="26" height="21" viewBox="0 0 26 21" fill="none">
                                    <path d="M13 0L25.1244 21H0.875645L13 0Z" fill="white" />
                                </svg>
                            </div>
                            <div
                                className={`main-search-form--horizontal-rule ${isAccordionVisible || isSearchDock ? '' : 'collapsed'}`}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        closeAccordion();
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                onClick={closeAccordion}
                                aria-label="Close"
                            >
                                <hr />
                            </div>
                            <div
                                className={mobileSearchFormCloseButtonDivClasses}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        toggleAccordion();
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                onClick={toggleAccordion}
                            >
                                {/* Smanjena kartica mobile */}
                                <div className="main-search-form-accordion-button--left">
                                    <p className="main-search-form-accordion-button--left-p main-search-form-accordion-button--left-location">
                                        {searchString || staticPublicResources.desktop.locationLabel}
                                    </p>
                                    <p className="main-search-form-accordion-button--left-p main-search-form-accordion-button--left-date-guests">
                                        {!searchString && !endDate && persons === 0 && (
                                            <span>
                                                <span>{staticPublicResources.desktop.datePickLabel}&nbsp;</span>
                                                <span>{staticPublicResources.desktop.numberOfGuestsLabel}</span>
                                            </span>
                                        )}
                                        {endDate && (
                                            <span>
                                                {renderFormattedInterval}
                                                {persons > 0 && <span> | </span>}
                                            </span>
                                        )}
                                        {persons > 0 && getSelectedGuestsDisplayText()}
                                    </p>
                                </div>
                                <div className="main-search-form-accordion-button--right">
                                    <RiPencilLine className="main-search-form-accordion-button--right-svg main-search-form-accordion-button__right-icon" />
                                </div>
                            </div>
                            <div className={mobileSearchFormDivClasses}>
                                {isSearchDock && (
                                    <div className="main-search-form-accordion__header">
                                        <p className="main-search-form-accordion__header-title">{staticPublicResources.common.SEOTitle}</p>
                                    </div>
                                )}
                                <div
                                    className={`main-search-form-accordion__item ${searchString && !selectedLocation ? 'main-search-form-accordion__item--error' : ''}`}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            openLocationModal();
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openLocationModal}
                                >
                                    <div className="main-search-form-accordion__item-left">
                                        <MdOutlinePlace className="main-search-form-accordion__item-left-svg main-search-form-accordion__item-icon" />
                                        {(isHome || isHomeForFrame || isWishlist || isPillarPage) && (
                                            selectedLocation ? (
                                                <span className="main-search-form-accordion__item-label" title={selectedLocation} dangerouslySetInnerHTML={{ __html: selectedLocationLabel }} />
                                            ) : (
                                                <span className="main-search-form-accordion__item-label">
                                                    {searchString || (
                                                        <>
                                                            <strong className="main-search-form-accordion__item-label-strong">{staticPublicResources.desktop.locationLabel}</strong><br /><span title={locationPublicResources.inputUsageInstructions}>{locationPublicResources.inputUsageInstructions}</span>
                                                        </>
                                                    )}
                                                </span>
                                            )
                                        )}
                                        {isSearchResults && (
                                            <span className="main-search-form-accordion__item-label">
                                                <strong className="main-search-form-accordion__item-label-strong">{staticPublicResources.desktop.locationLabel}</strong>
                                                <br />
                                                <span title={searchString || locationPublicResources.inputUsageInstructions}>
                                                    {searchString || locationPublicResources.inputUsageInstructions}
                                                </span>
                                            </span>
                                        )}
                                        {isSearchDock && (
                                            searchString ? (
                                                <span>{searchString}</span>
                                            ) : (
                                                <span>{staticPublicResources.desktop.locationLabel}</span>
                                            )
                                        )}
                                    </div>
                                    {(isSearchResults || isSearchDock) && (
                                        <div className="main-search-form-accordion__item-right">
                                            <RiPencilLine className="main-search-form-accordion__item-right-svg main-search-form-accordion__item-right-icon" />
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={`main-search-form-accordion__item ${isFlexible && !!endDate !== !!f_length ? 'main-search-form-accordion__item--error' : ''}`}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            openDatesModal();
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openDatesModal}
                                    id={DATES_SELECT_SEARCH_DOCK_ID}
                                >
                                    <div className="main-search-form-accordion__item-left">
                                        <MdOutlineCalendarToday className="main-search-form-accordion__item-left-svg main-search-form-accordion__item-icon" />
                                        {!isSearchDock && (
                                            <div className="main-search-form-accordion__item-label">
                                                <strong className="main-search-form-accordion__item-label-strong">{staticPublicResources.desktop.datePickLabel}</strong>
                                                <br />
                                                {endDate ? renderFormattedInterval : (
                                                    <span>
                                                        {staticPublicResources.desktop.datePickLongLabel}
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        {isSearchDock && (
                                            endDate ? renderFormattedInterval : (
                                                <span>
                                                    {staticPublicResources.desktop.datePickLabel}
                                                </span>
                                            )
                                        )}
                                    </div>
                                    {(isSearchResults || isSearchDock) && (
                                        <div className="main-search-form-accordion__item-right">
                                            <RiPencilLine className="main-search-form-accordion__item-right-svg main-search-form-accordion__item-right-icon" />
                                        </div>
                                    )}
                                </div>
                                <div
                                    className="main-search-form-accordion__item"
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            openGuestsModal();
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openGuestsModal}
                                    id={GUEST_SELECT_SEARCH_DOCK_ID}
                                >
                                    {/* Mobilni search bar */}
                                    <div className="main-search-form-accordion__item-left">
                                        <MdPersonOutline className="main-search-form-accordion__item-left-svg main-search-form-accordion__item-icon" />
                                        {(isHome || isHomeForFrame || isWishlist || isPillarPage || isSearchResults) && (
                                            <div className="main-search-form-accordion__item-label">
                                                <strong className="main-search-form-accordion__item-label-strong">{staticPublicResources.desktop.numberOfGuestsLabel}</strong>
                                                <br />
                                                {getSelectedGuestsDisplayText()}
                                            </div>
                                        )}
                                        {isSearchDock && (
                                            persons > guestsPublicResources.adults.minimumNumber ? (
                                                <span>
                                                    {` ${guestsPublicResources.selectedNumberOfGuestsLabel} ${persons}`}
                                                </span>
                                            ) : (
                                                <span>
                                                    {staticPublicResources.desktop.numberOfGuestsLabel}
                                                </span>
                                            )
                                        )}
                                    </div>
                                    {(isSearchResults || isSearchDock) && (
                                        <div className="main-search-form-accordion__item-right">
                                            <RiPencilLine className="main-search-form-accordion__item-right-svg main-search-form-accordion__item-right-icon" />
                                        </div>
                                    )}
                                </div>
                                {(isHome || isHomeForFrame || isWishlist || isPillarPage) ? (
                                    <button
                                        type="button"
                                        aria-label="Submit Search"
                                        className="adr-btn-primary-lg"
                                        disabled={shouldDisableSubmitSearch()}
                                        onClick={() => {
                                            submitSearch([
                                                {
                                                    name: SEARCH_ACTION,
                                                    payload: {
                                                        location_id: locationURL,
                                                        from: formatDateForRequest(startDate),
                                                        to: formatDateForRequest(endDate),
                                                        p_dist,
                                                        persons_adult,
                                                        persons_12_17,
                                                        persons_4_11,
                                                        persons_0_3,
                                                        ...getFlexibleDataForSubmit(),
                                                    },
                                                },
                                                { name: isHome ? HOMEPAGE_QUICK_SEARCH_SUBMIT : PILLAR_PAGE_QUICK_SEARCH_SUBMIT },
                                            ]);
                                        }}
                                    >
                                        <MdOutlineSearch className="main-search-form-accordion__search-button-icon" />
                                        <span className="main-search-form-accordion__search-button-text">{staticPublicResources.common.searchLabel}</span>
                                    </button>
                                ) : (
                                    <button type="button" className="main-search-form-accordion__close" onClick={toggleAccordion} aria-label="Close">
                                        {staticPublicResources.mobile.closeLabel}
                                    </button>
                                )}
                                <button
                                    className="adr-btn-primary-md"
                                    type="button"
                                    aria-label="Submit Search"
                                    onClick={() => submitSearch([{ name: MOBILE_DOCK_QUICK_SEARCH_SUBMIT }])}
                                >
                                    {staticPublicResources.common.searchLabel}
                                </button>
                            </div>
                            <div
                                className={`inputLocationOuter d-lg-block d-none ${showLocation ? 'inputLocationOuter--shown' : ''} ${selectedLocation ? 'inputLocationOuter--selected' : ''} ${searchString && !selectedLocation ? 'inputLocationOuter--error' : ''}`}
                                onBlur={handleBlurLocationsDesktop}
                                onFocus={(event) => handleFocus(event, setShowLocation)}
                                tabIndex={-1}
                            >
                                <div
                                    role={isHomeOrSearchResultsOrWishlistOrPillarPage ? 'button' : null}
                                    className="input-location-inner"
                                    onFocus={(event) => {
                                        if (isHomeOrSearchResultsOrWishlistOrPillarPage) {
                                            handleFocus(event, setShowLocation);
                                        }
                                    }}
                                    // Napravljeno ovako kako HTML validator ne bi dojavljivao greške zbog <input> unutar role="button" elementa.
                                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                                    tabIndex={isHomeOrSearchResultsOrWishlistOrPillarPage ? 0 : null}
                                >
                                    <MdOutlinePlace className="input-location__icon" />
                                    <div className="input-location__wrapper">
                                        <input
                                            ref={locationInput}
                                            autoComplete={isInputLocationTypeHidden ? null : 'off'}
                                            className={`inputLocation ${showLocation ? 'inputLocation--shown' : ''} ${selectedLocation ? 'inputLocation--selected' : ''} ${searchString && !selectedLocation ? 'inputLocation--error' : ''}`}
                                            name="location"
                                            type={isInputLocationTypeHidden ? 'hidden' : 'text'}
                                            placeholder={isInputLocationTypeHidden ? null : staticPublicResources.desktop.locationLabel}
                                            value={searchString || ''}
                                            onChange={handleLocationInputChange}
                                            onKeyDown={handleLocationInputKeyDown}
                                        />
                                        {isHomeOrSearchResultsOrWishlistOrPillarPage && (
                                            selectedLocation ? (
                                                <span className="input-location__wrapper-label" title={selectedLocation} dangerouslySetInnerHTML={{ __html: selectedLocationLabel }} />
                                            ) : (
                                                <span className="input-location__wrapper-label">
                                                    {searchString || (
                                                        <>
                                                            <strong className="input-location__wrapper-label-strong">{staticPublicResources.desktop.locationLabel}</strong><br /><span title={locationPublicResources.inputUsageInstructions}>{locationPublicResources.inputUsageInstructions}</span>
                                                        </>
                                                    )}
                                                </span>
                                            )
                                        )}
                                    </div>
                                    {searchString && !selectedLocation && !showLocation && (
                                        <span className="location-error-message">
                                            {locationPublicResources.incorrectLocationLabelErrorString}
                                        </span>
                                    )}
                                    {searchString && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchString('');
                                                setShowLocation(true);
                                                setLocationURL('');
                                                setSelectedLocation(null);
                                                setSelectedLocationLabel(null);
                                                setFocusedLocationIndex(null);
                                                setSearchedArray(allLocations ?? []);
                                                locationInput.current.focus();
                                            }}
                                            aria-label="Clear Locations"
                                            className="date-range-picker-input__clear-dates"
                                        >
                                            <IoMdCloseCircleOutline className="date-range-picker-input__clear-dates-svg" />
                                        </button>
                                    )}
                                </div>
                                <div
                                    ref={locationDropdownDesktopRef}
                                    className={`locationDropdown ${showLocation ? 'active' : ''}`}
                                >
                                    {locationBlock}
                                </div>
                            </div>
                            <div
                                ref={inputDateRangeRef}
                                className={`inputDateRangeOuter d-lg-block d-none ${showDateRange ? 'inputDateRangeOuter--shown' : ''} ${endDate ? 'inputDateRangeOuter--selected' : ''} ${isFlexible && !!endDate !== !!f_length ? 'inputDateRangeOuter--error' : ''}`}
                                onFocus={(event) => handleFocus(event, setShowDateRange)}
                                onBlur={handleBlurDateRange}
                                tabIndex={-1}
                                id={DATES_SELECT_SEARCH_FORM_ID}
                            >
                                <div
                                    role="button"
                                    className="input-date-range-inner"
                                    onFocus={(event) => handleFocus(event, setShowDateRange)}
                                    tabIndex={0}
                                >
                                    <MdOutlineCalendarToday className="input-date-range__icon" />
                                    {isHomeOrSearchResultsOrWishlistOrPillarPage ? (
                                        <div className="input-date-range__label">
                                            <strong className="input-date-range__label-strong">{staticPublicResources.desktop.datePickLabel}</strong>
                                            <br />
                                            {endDate ? renderFormattedInterval : (
                                                <span>
                                                    {staticPublicResources.desktop.datePickLongLabel}
                                                </span>
                                            )}
                                        </div>
                                    ) : (
                                        <span className={`inputDateRange inputDateRange__text ${showDateRange ? 'inputDateRange--shown' : ''} ${endDate ? 'inputDateRange--selected' : ''}`}>
                                            {endDate
                                                ? renderFormattedInterval
                                                : staticPublicResources.desktop.datePickLabel}
                                        </span>
                                    )}
                                    <input
                                        name="dateRange"
                                        type="hidden"
                                        value={endDate
                                            ? `${startDateFormatted} - ${endDateFormatted}`
                                            : ''}
                                    />
                                    {!showDateRange && isFlexible && !!endDate !== !!f_length && (
                                        <span className="location-error-message">
                                            {locationPublicResources.incorrectLocationLabelErrorString}
                                        </span>
                                    )}
                                    {(endDate || (isFlexible && f_length > 0)) && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setStartDate(null);
                                                setEndDate(null);
                                                setShowDateRange(true);
                                                setFLength(0);
                                                setIntervalDeviation(0);
                                                setSelectedDatesMemo({
                                                    startDate: null,
                                                    endDate: null,
                                                });
                                                inputDateRangeRef.current.focus();
                                            }}
                                            aria-label="Clear Dates"
                                            className="date-range-picker-input__clear-dates"
                                        >
                                            <IoMdCloseCircleOutline className="date-range-picker-input__clear-dates-svg" />
                                        </button>
                                    )}
                                </div>
                                {!isSmallDevice && showDateRange && (
                                    <div className={`input-date-range__dropdown ${!showDateRange ? 'd-none' : ''}`}>
                                        {renderDatePickerTopContent}
                                        <div className="input-date-range__dropdown-main-content">
                                            <DateRangePicker
                                                isDatePickerShown={showDateRange}
                                                initialMonth={calendarStartingMonth}
                                                startDate={startDate}
                                                endDate={endDate}
                                                onDatesChange={(newStartDate, newEndDate) => {
                                                    setStartDate(newStartDate);
                                                    setEndDate(newEndDate);
                                                    setShouldShowLessThanMinOvernightStaysError(false);
                                                    setShouldShowMoreThanMaxOvernightStaysError(false);
                                                }}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                orientation={orientation}
                                                desktopNumberOfDisplayedMonths={numberOfMonthsDisplayedDesktop}
                                                notAllowedToSelectMessage={datePublicResources?.notAllowedToSelectMessage}
                                                ssrParameters={ssrParameters}
                                                minOvernightStays={isFlexible ? f_length : null}
                                                maxOvernightStays={isFlexible
                                                    ? datePublicResources.fleksibleInterval.maximalIntervalLength
                                                    : null}
                                                onClickDayLessThanMinOvernightStays={onClickDayLessThanMinOvernightStays}
                                                onClickDayMoreThanMaxOvernightStays={onClickDayMoreThanMaxOvernightStays}
                                            />
                                            {renderDatePickerBottomContent}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div
                                ref={guestsInput}
                                className={`inputGuestsOuter d-lg-block d-none ${showGuests ? 'inputGuestsOuter--shown' : ''} ${persons > guestsPublicResources.adults.minimumNumber ? 'inputGuestsOuter--selected' : ''}`}
                                onFocus={(event) => handleFocus(event, setShowGuests)}
                                onBlur={handleBlurGuests}
                                tabIndex={-1}
                                id={GUEST_SELECT_SEARCH_FORM_ID}
                            >
                                <div
                                    role="button"
                                    className="input-guests-inner"
                                    onFocus={(event) => handleFocus(event, setShowGuests)}
                                    tabIndex={0}
                                >
                                    <MdPersonOutline className="input-guests__icon" />
                                    {isHomeOrSearchResultsOrWishlistOrPillarPage ? (
                                        // Naslovna ili pillar - desktop
                                        <div className="input-guests__label">
                                            <strong className="input-guests__label-strong">{staticPublicResources.desktop.numberOfGuestsLabel}</strong>
                                            <br />
                                            {getSelectedGuestsDisplayText()}
                                        </div>
                                    ) : (
                                        <span className={`inputGuests inputGuests__text ${showGuests ? 'inputGuests--shown' : ''} ${persons > guestsPublicResources.adults.minimumNumber ? 'inputGuests--selected' : ''}`}>
                                            {persons > guestsPublicResources.adults.minimumNumber
                                                ? getSelectedGuestsDisplayText(true)
                                                : getInputGuestsPlaceholder()}
                                        </span>
                                    )}
                                    <input
                                        name="guests"
                                        type="hidden"
                                        value={
                                            persons > guestsPublicResources.adults.minimumNumber
                                                ? getSelectedGuestsDisplayText()
                                                : ''
                                        }
                                    />
                                    {persons > guestsPublicResources.adults.minimumNumber && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setPersonsAdult(guestsPublicResources.adults.minimumNumber);
                                                setPersons12_17(0);
                                                setPersons4_11(0);
                                                setPersons0_3(0);
                                                setPDist(0);
                                                setShowGuests(true);
                                                guestsInput.current.focus();
                                            }}
                                            aria-label="Clear Guests"
                                            className="date-range-picker-input__clear-dates"
                                        >
                                            <IoMdCloseCircleOutline className="date-range-picker-input__clear-dates-svg" />
                                        </button>
                                    )}
                                </div>
                                <div className={`guestsDropdown ${showGuests ? 'active' : ''}`}>
                                    <p className="guests-intro-text">{guestsPublicResources.chooseLabel}</p>
                                    {guestsBlock('mobile')}
                                </div>
                            </div>
                            {(isPillarPage || isHomeForFrame) ? (
                                <button
                                    type="button"
                                    aria-label="Submit Search"
                                    className="adr-btn-primary-md d-lg-flex d-none"
                                    disabled={shouldDisableSubmitSearch()}
                                    onClick={() => {
                                        submitSearch([
                                            {
                                                name: SEARCH_ACTION,
                                                payload: {
                                                    location_id: locationURL,
                                                    from: formatDateForRequest(startDate),
                                                    to: formatDateForRequest(endDate),
                                                    p_dist,
                                                    persons_adult,
                                                    persons_12_17,
                                                    persons_4_11,
                                                    persons_0_3,
                                                    ...getFlexibleDataForSubmit(),
                                                },
                                            },
                                            { name: PILLAR_PAGE_QUICK_SEARCH_SUBMIT },
                                        ]);
                                    }}
                                >
                                    <MdOutlineSearch className="main-search-form-accordion__search-button-icon" />
                                    <span className="main-search-form-accordion__search-button-text">{staticPublicResources.common.searchLabel}</span>
                                </button>
                            ) : (
                                <button
                                    className="submit-search d-lg-flex d-none"
                                    type="button"
                                    aria-label="Submit Search"
                                    disabled={shouldDisableSubmitSearch()}
                                    onClick={() => {
                                        submitSearch([
                                            {
                                                name: SEARCH_ACTION,
                                                payload: {
                                                    location_id: locationURL,
                                                    from: formatDateForRequest(startDate),
                                                    to: formatDateForRequest(endDate),
                                                    p_dist,
                                                    persons_adult,
                                                    persons_12_17,
                                                    persons_4_11,
                                                    persons_0_3,
                                                    ...getFlexibleDataForSubmit(),
                                                },
                                            },
                                            { name: isHome ? HOMEPAGE_QUICK_SEARCH_SUBMIT : SEARCH_RESULTS_QUICK_SEARCH_SUBMIT },
                                        ]);
                                    }}
                                >
                                    <MdOutlineSearch className="submit-search-svg" />
                                    {(isHome || isWishlist) && (
                                        <span className="submit-search__text">
                                            {staticPublicResources.common.searchLabel}
                                        </span>
                                    )}
                                </button>
                            )}
                        </form>
                    </div>
                </>
            )}
        </>
    );
}
